import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import * as XLSX from 'xlsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { reportCreatorI18n } from '../i18n';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend
);

const PreviewPanel = ({
  DesignSystemComponents,
  preview,
  chartView,
  onChartViewChange,
  chartSupported,
  definition,
  isRunning,
  onRunReport,
  onCancelRun,
}) => {
  const { Button, Table } = DesignSystemComponents ?? {};
  const { t } = useTranslation(undefined, { i18n: reportCreatorI18n });
  const [tablePage, setTablePage] = useState(0);
  const [tablePageSize, setTablePageSize] = useState(20);

  useEffect(() => {
    setTablePage(0);
  }, [preview.status, preview.rows.length]);

  const buildSynopsis = () => {
    if (!definition) return '--';
    const parts = [];
    if (definition.dimensions?.length) {
      parts.push(
        t('rc_preview_dimensions', {
          value: definition.dimensions.map((item) => item.alias || item.label).join(', '),
        })
      );
    }
    if (definition.metrics?.length) {
      parts.push(
        t('rc_preview_metrics', {
          value: definition.metrics
            .map(
              (item) =>
                `${item.aggregation || 'agg'}(${item.alias || item.label})`
            )
            .join(', '),
        })
      );
    }
    if (definition.filters?.length) {
      parts.push(t('rc_preview_filters', { count: definition.filters.length }));
    }
    if (definition.sort?.length) {
      parts.push(t('rc_preview_sort', { count: definition.sort.length }));
    }
    return parts.length ? parts.join('  |  ') : '--';
  };

  const renderChart = () => {
    if (!preview.rows.length || preview.columns.length < 2) return null;

    const labelColumn = preview.columns[0];
    const metricColumn = preview.columns[1];

    const labels = preview.rows.map((row) => String(row[labelColumn.accessor]));
    const values = preview.rows.map((row) =>
      Number(row[metricColumn.accessor] || 0)
    );

    const data = {
      labels,
      datasets: [
        {
          label: metricColumn.label,
          data: values,
          borderColor: '#0F766E',
          backgroundColor: 'rgba(15, 118, 110, 0.25)',
          borderWidth: 2,
        },
      ],
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top',
        },
      },
      scales: {
        y: { beginAtZero: true },
      },
    };

    if (chartView === 'line') {
      return <Line data={data} options={options} />;
    }

    return <Bar data={data} options={options} />;
  };

  const handleExportXlsx = () => {
    if (
      preview.status !== 'success' ||
      !preview.columns?.length ||
      !preview.rows?.length
    ) {
      return;
    }

    const exportRows = preview.rows.map((row) => {
      const orderedRow = {};
      preview.columns.forEach((column) => {
        orderedRow[column.label] = row[column.accessor] ?? '';
      });
      return orderedRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Resultado');
    const fileName = `Reporte_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <section className='report-preview report-creator-panel'>
      <div className='report-preview-head mb-3'>
        <div>
          <p className='caption color-gray-600 mb-0'>{buildSynopsis()}</p>
        </div>
        <div className='report-preview-controls'>
          {Button && (
            <Button
              label={t('rc_preview_btn_download')}
              variant='naked'
              size='sm'
              onClick={handleExportXlsx}
              disabled={preview.status !== 'success' || !preview.rows?.length}
            />
          )}
          {Button &&
            (isRunning ? (
              <Button
                label={t('rc_preview_btn_cancel')}
                variant='secondary'
                size='sm'
                onClick={onCancelRun}
              />
            ) : (
              <Button
                label={t('rc_preview_btn_run')}
                variant='primary'
                size='sm'
                onClick={onRunReport}
              />
            ))}
        </div>
      </div>

      <div className='d-flex justify-content-between align-items-center mb-3'>
        <h3 className='fw-semibold mb-0'>{t('rc_preview_result_title')}</h3>
        {chartSupported && (
          <div className='d-flex gap-2'>
            {['table', 'bar', 'line'].map((view) => (
              <Button
                key={view}
                size='sm'
                variant={chartView === view ? 'primary' : 'secondary'}
                label={
                  view === 'table'
                    ? t('rc_preview_view_table')
                    : view === 'bar'
                      ? t('rc_preview_view_bar')
                      : t('rc_preview_view_line')
                }
                onClick={() => onChartViewChange(view)}
              />
            ))}
          </div>
        )}
      </div>

      {preview.status === 'idle' && (
        <p className='caption color-gray-600 mb-0'>
          {t('rc_preview_idle')}
        </p>
      )}
      {preview.status === 'loading' && (
        <p className='caption color-gray-600 mb-0'>
          {t('rc_preview_loading')}
        </p>
      )}
      {preview.status === 'invalid' && (
        <p className='caption text-danger mb-0'>
          {t('rc_preview_invalid')}
        </p>
      )}
      {preview.status === 'empty' && (
        <p className='caption color-gray-600 mb-0'>
          {t('rc_preview_empty')}
        </p>
      )}
      {preview.status === 'no-data' && (
        <p className='caption color-gray-600 mb-0'>
          {t('rc_preview_no_data')}
        </p>
      )}
      {preview.status === 'error' && (
        <p className='caption text-danger mb-0'>
          {preview.errorMessage || t('rc_preview_error_fallback')}
        </p>
      )}

      {preview.status === 'success' && (
        <>
          {chartView === 'table' ? (
            Table ? (
              <div className='report-preview-table'>
                <Table
                  columns={preview.columns}
                  data={preview.rows}
                  paginationMode='client'
                  page={tablePage}
                  onPageChange={setTablePage}
                  paginator
                  paginatorGroup={[
                    { label: '10', value: 10 },
                    { label: '20', value: 20 },
                    { label: '50', value: 50 },
                    { label: '100', value: 100 },
                  ]}
                  pageSize={tablePageSize}
                  onPageSizeChange={(newSize) => {
                    setTablePageSize(newSize);
                    setTablePage(0);
                  }}
                  totalItems={preview.rows.length}
                />
              </div>
            ) : (
              <div className='table-responsive'>
                <table className='table table-sm'>
                  <thead>
                    <tr>
                      {preview.columns.map((column) => (
                        <th key={column.accessor}>{column.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row, rowIndex) => (
                      <tr key={`row-${rowIndex}`}>
                        {preview.columns.map((column) => (
                          <td key={`${rowIndex}-${column.accessor}`}>
                            {String(row[column.accessor] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            <div className='report-preview-chart'>{renderChart()}</div>
          )}
          <div className='report-preview-footer mt-3'>
            <span className='caption color-gray-600'>
              {t('rc_preview_footer', {
                recordsFound: preview.recordsFound,
                rowCount: preview.rowCount,
                executionMs: preview.executionMs,
              })}
            </span>
          </div>
        </>
      )}
    </section>
  );
};

export default PreviewPanel;
